import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import apiService from '../services/api';

function DrugDetail() {
  const { collectionId } = useParams();
  const [activeTab, setActiveTab] = useState('products');
  const [drug, setDrug] = useState(null);
  const [tabData, setTabData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDrugDetails();
  }, [collectionId]);

  const fetchDrugDetails = async () => {
    try {
      setLoading(true);
      const response = await apiService.getDrugDetails(collectionId);
      setDrug(response.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadTabData = async (tab) => {
    if (tabData[tab]) return; // Already loaded

    try {
      let response;
      switch (tab) {
        case 'products':
          response = await apiService.getDrugProducts(collectionId);
          break;
        case 'sideEffects':
          response = await apiService.getSideEffects(collectionId);
          break;
        case 'diseaseIndications':
          response = await apiService.getDiseaseIndications(collectionId);
          break;
        case 'phenotypeIndications':
          response = await apiService.getPhenotypeIndications(collectionId);
          break;
        case 'drugIndications':
          response = await apiService.getDrugIndications(collectionId);
          break;
        case 'diseaseContraindications':
          response = await apiService.getDiseaseContraindications(collectionId);
          break;
        case 'phenotypeContraindications':
          response = await apiService.getPhenotypeContraindications(collectionId);
          break;
        case 'drugContraindications':
          response = await apiService.getDrugContraindications(collectionId);
          break;
        case 'evidence':
          response = await apiService.getEvidence(collectionId);
          break;
        default:
          return;
      }
      setTabData(prev => ({ ...prev, [tab]: response.data.results }));
    } catch (err) {
      console.error(`Failed to load ${tab}:`, err);
    }
  };

  useEffect(() => {
    if (drug) {
      loadTabData(activeTab);
    }
  }, [activeTab, drug]);

  if (loading) {
    return (
      <div className="container">
        <div className="loading">Loading drug details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  if (!drug) {
    return (
      <div className="container">
        <div className="error">Drug not found</div>
      </div>
    );
  }

  const tabs = [
    { id: 'products', label: 'Products', icon: '📦' },
    { id: 'sideEffects', label: 'Side Effects', icon: '⚠️' },
    { id: 'diseaseIndications', label: 'Disease Indications', icon: '✅' },
    { id: 'phenotypeIndications', label: 'Phenotype Indications', icon: '✅' },
    { id: 'drugIndications', label: 'Drug Indications', icon: '💊' },
    { id: 'diseaseContraindications', label: 'Disease Contraindications', icon: '🚫' },
    { id: 'phenotypeContraindications', label: 'Phenotype Contraindications', icon: '🚫' },
    { id: 'drugContraindications', label: 'Drug Contraindications', icon: '🚫' },
    { id: 'evidence', label: 'Evidence', icon: '📄' },
  ];

  return (
    <div className="container">
      {/* Header */}
      <div className="card">
        <h1 style={{ marginBottom: '0.5rem' }}>
          💊 {drug.label}
        </h1>
        {drug.rxcuis && drug.rxcuis.length > 0 && (
          <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
            RxCUIs: {drug.rxcuis.join(', ')}
          </p>
        )}
        {drug.counts && (
          <div style={{
            display: 'flex',
            gap: '1rem',
            flexWrap: 'wrap',
            fontSize: '0.875rem'
          }}>
            {Object.entries(drug.counts).map(([type, count]) => (
              <span key={type} className="badge badge-primary">
                {type.replace(/([A-Z])/g, ' $1').trim()}: {count}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="card">
        {activeTab === 'products' && (
          <ProductsTab data={tabData.products} />
        )}
        {activeTab === 'sideEffects' && (
          <SideEffectsTab data={tabData.sideEffects} />
        )}
        {activeTab === 'diseaseIndications' && (
          <DiseaseIndicationsTab data={tabData.diseaseIndications} />
        )}
        {activeTab === 'phenotypeIndications' && (
          <PhenotypeIndicationsTab data={tabData.phenotypeIndications} />
        )}
        {activeTab === 'drugIndications' && (
          <DrugIndicationsTab data={tabData.drugIndications} />
        )}
        {activeTab === 'diseaseContraindications' && (
          <DiseaseContraindicationsTab data={tabData.diseaseContraindications} />
        )}
        {activeTab === 'phenotypeContraindications' && (
          <PhenotypeContraindicationsTab data={tabData.phenotypeContraindications} />
        )}
        {activeTab === 'drugContraindications' && (
          <DrugContraindicationsTab data={tabData.drugContraindications} />
        )}
        {activeTab === 'evidence' && (
          <EvidenceTab data={tabData.evidence} />
        )}
      </div>
    </div>
  );
}

// Tab Components
function ProductsTab({ data }) {
  if (!data) return <div className="loading">Loading products...</div>;
  if (data.length === 0) return <p>No products found.</p>;

  return (
    <table>
      <thead>
        <tr>
          <th>Product Name</th>
          <th>RxCUI</th>
          <th>SPL Document</th>
        </tr>
      </thead>
      <tbody>
        {data.map((item, index) => (
          <tr key={index}>
            <td>{item.name}</td>
            <td>{item.rxcui || '-'}</td>
            <td>
              {item.spl_id ? (
                <a
                  href={`https://dailymed.nlm.nih.gov/dailymed/lookup.cfm?setid=${item.spl_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link"
                >
                  View SPL
                </a>
              ) : '-'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function SideEffectsTab({ data }) {
  if (!data) return <div className="loading">Loading side effects...</div>;
  if (data.length === 0) return <p>No side effects found.</p>;

  return (
    <table>
      <thead>
        <tr>
          <th>Side Effect (HPO)</th>
          <th>HPO ID</th>
        </tr>
      </thead>
      <tbody>
        {data.map((item, index) => (
          <tr key={index}>
            <td>{item.label}</td>
            <td>
              <a
                href={`https://hpo.jax.org/app/browse/term/${item.hpo_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="link"
              >
                {item.hpo_id}
              </a>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function DiseaseIndicationsTab({ data }) {
  if (!data) return <div className="loading">Loading disease indications...</div>;
  if (data.length === 0) return <p>No disease indications found.</p>;

  return (
    <table>
      <thead>
        <tr>
          <th>Disease (MONDO)</th>
          <th>MONDO ID</th>
        </tr>
      </thead>
      <tbody>
        {data.map((item, index) => (
          <tr key={index}>
            <td>{item.label}</td>
            <td>
              <a
                href={`https://www.ebi.ac.uk/ols/ontologies/mondo/terms?iri=http://purl.obolibrary.org/obo/${item.mondo_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="link"
              >
                {item.mondo_id}
              </a>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function PhenotypeIndicationsTab({ data }) {
  if (!data) return <div className="loading">Loading phenotype indications...</div>;
  if (data.length === 0) return <p>No phenotype indications found.</p>;

  return (
    <table>
      <thead>
        <tr>
          <th>Phenotype (HPO)</th>
          <th>HPO ID</th>
        </tr>
      </thead>
      <tbody>
        {data.map((item, index) => (
          <tr key={index}>
            <td>{item.label}</td>
            <td>
              <a
                href={`https://hpo.jax.org/app/browse/term/${item.hpo_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="link"
              >
                {item.hpo_id}
              </a>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function DrugIndicationsTab({ data }) {
  if (!data) return <div className="loading">Loading drug indications...</div>;
  if (data.length === 0) return <p>No drug indications found.</p>;

  return (
    <table>
      <thead>
        <tr>
          <th>Use With Drug Collection</th>
          <th>RxCUIs</th>
        </tr>
      </thead>
      <tbody>
        {data.map((item, index) => (
          <tr key={index}>
            <td>
              <Link to={`/drug/${item.id}`} className="link">
                {item.label}
              </Link>
            </td>
            <td>{item.rxcuis ? item.rxcuis.join(', ') : '-'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function DiseaseContraindicationsTab({ data }) {
  if (!data) return <div className="loading">Loading disease contraindications...</div>;
  if (data.length === 0) return <p>No disease contraindications found.</p>;

  return (
    <table>
      <thead>
        <tr>
          <th>⚠️ Contraindicated In Disease</th>
          <th>MONDO ID</th>
        </tr>
      </thead>
      <tbody>
        {data.map((item, index) => (
          <tr key={index} style={{ backgroundColor: 'rgba(239, 68, 68, 0.05)' }}>
            <td>{item.label}</td>
            <td>
              <a
                href={`https://www.ebi.ac.uk/ols/ontologies/mondo/terms?iri=http://purl.obolibrary.org/obo/${item.mondo_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="link"
              >
                {item.mondo_id}
              </a>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function PhenotypeContraindicationsTab({ data }) {
  if (!data) return <div className="loading">Loading phenotype contraindications...</div>;
  if (data.length === 0) return <p>No phenotype contraindications found.</p>;

  return (
    <table>
      <thead>
        <tr>
          <th>⚠️ Contraindicated In Phenotype</th>
          <th>HPO ID</th>
        </tr>
      </thead>
      <tbody>
        {data.map((item, index) => (
          <tr key={index} style={{ backgroundColor: 'rgba(239, 68, 68, 0.05)' }}>
            <td>{item.label}</td>
            <td>
              <a
                href={`https://hpo.jax.org/app/browse/term/${item.hpo_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="link"
              >
                {item.hpo_id}
              </a>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function DrugContraindicationsTab({ data }) {
  if (!data) return <div className="loading">Loading drug contraindications...</div>;
  if (data.length === 0) return <p>No drug contraindications found.</p>;

  return (
    <table>
      <thead>
        <tr>
          <th>🚫 DO NOT USE WITH</th>
          <th>RxCUIs</th>
        </tr>
      </thead>
      <tbody>
        {data.map((item, index) => (
          <tr key={index} style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}>
            <td>
              <Link to={`/drug/${item.id}`} className="link" style={{ color: 'var(--danger)' }}>
                {item.label}
              </Link>
            </td>
            <td>{item.rxcuis ? item.rxcuis.join(', ') : '-'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function EvidenceTab({ data }) {
  if (!data) return <div className="loading">Loading evidence...</div>;
  if (data.length === 0) return <p>No evidence found.</p>;

  return (
    <table>
      <thead>
        <tr>
          <th>SPL Set ID</th>
          <th>Version</th>
          <th>Link</th>
        </tr>
      </thead>
      <tbody>
        {data.map((item, index) => (
          <tr key={index}>
            <td style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>{item.spl_id}</td>
            <td>{item.spl_version || '-'}</td>
            <td>
              <a
                href={item.dailymed_url}
                target="_blank"
                rel="noopener noreferrer"
                className="link"
              >
                View on DailyMed
              </a>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default DrugDetail;
